/**
 * App shell.
 *
 * Responsibilities:
 *   - Top-level view routing (no react-router; the portal is shallow)
 *   - Global keyboard shortcuts (⌘K, ⌘0–⌘9, ⌘L, ?)
 *   - Persistent view across refresh (localStorage)
 *   - Hosts Topbar, Sidebar, toasts, command palette, help overlay
 *
 * Routes (post 2026-06-09 cleanup — removed Preston's prototype mock pages):
 *   - `live`           : Live Sessions  (site admin)
 *   - `workforce`      : Workforce      (site admin)
 *   - `cohort-builder` : Orientation Assignment (site admin; label renamed from Cohort Builder 2026-09-10)
 *   - `review-queue`   : Review Queue   (site admin)
 *   - `leadership`     : Leadership     (site admin)
 *   - `members`        : WorkOS Members (site admin)
 *   - `orientations`, `orientation-editor` (author)
 *   - `trainee`        : My Orientations (everyone)
 *   - `settings`       : Settings       (everyone)
 *
 * Rights per page: lib/siteAccess (ORIENTATION-ROLES P2). Default landing
 * follows the highest right: Leadership, Orientations, else Trainee.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sidebar, Topbar, Toasts, CmdPalette, HelpOverlay } from './components/chrome';
import { BugReportDialog } from './components/BugReport/BugReportDialog';
import { Settings } from './components/Settings/Settings';
import { Members } from './components/Members/Members';
import { Workforce } from './components/Workforce/Workforce';
import { CohortBuilder } from './components/CohortBuilder/CohortBuilder';
import { Cohorts } from './components/Cohorts/Cohorts';
import { OrientationEditor } from './components/OrientationEditor/OrientationEditor';
import { Orientations } from './components/Orientations/Orientations';
import { ReviewQueue } from './components/ReviewQueue/ReviewQueue';
import { LiveSessions } from './components/LiveSessions/LiveSessions';
import { Leadership } from './components/Leadership/Leadership';
import { TraineePortal } from './components/Trainee/TraineePortal';
import { useAuth } from './hooks/useAuth';
import { useAuthKit } from './contexts/AuthProvider';
import { useOrgSession } from './contexts/OrgContext';
import { urlSlugFromOrgSlug } from './services/orgFromUrl';
import { useDisplayNameSync } from './hooks/useDisplayNameSync';
import { useLauncherCorsSelfAllowlist } from './hooks/useLauncherCorsSelfAllowlist';
import { useTweaks } from './hooks/useTweaks';
import { useToasts } from './hooks/useToasts';
import { useDurableNotifications } from './hooks/useDurableNotifications';
import { useProjects } from './hooks/useProjects';
import { useSiteAccess } from './hooks/useSiteAccess';
import { canOpenView, landingView } from './lib/siteAccess';
import { StuckProvisioningBanner } from './components/StuckProvisioningBanner';
import { ConnectionBanner } from './components/ConnectionBanner';
import type { Site, ViewKey } from './data/types';

const VIEW_STORAGE_KEY = 'mz_view';

// Default site policy: when a user has no explicit selection
// (tweaks.siteIndex === -1, the sentinel from useTweaks), prefer the
// site whose project name matches this. Falls back to sites[0] if not
// present in the user's project list.
const DEFAULT_SITE_PROJECT = 'minezero-sandbox';

function findDefaultSiteIndex(sites: readonly Site[]): number {
  const idx = sites.findIndex((s) => s.projectName === DEFAULT_SITE_PROJECT);
  return idx >= 0 ? idx : 0;
}

type ViewState = { view: ViewKey; ctx: { payload?: any;[k: string]: any } };

/** `null` when nothing is stored: the caller lands by the user's rights. */
function readStoredView(landing: ViewKey): ViewState | null {
  try {
    const raw = localStorage.getItem(VIEW_STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    return { view: o.view || landing, ctx: o.ctx || {} };
  } catch {
    return { view: landing, ctx: {} };
  }
}

// ---------------------------------------------------------------------------
// URL sync: `/<orgSlug>/<siteSlug>/<view>`.
//
// The path mirrors the topbar context so coordinators can tell at a
// glance which org and site they're operating in (the org segment is
// stable for the session; the site segment swaps when they pick a
// different site from the topbar; the view segment swaps when they
// navigate). Browser back/forward walks both site and view changes.
//
// Slug derivation:
//   - org    → ORG_SLUG_MAP[organizationId] when known; else a
//              shortened, lower-cased version of the org_id so the
//              URL still reads as something rather than nothing.
//   - site   → `Site.projectName` verbatim (already URL-friendly:
//              kebab-case, no spaces; e.g. `samuel-sandbox`).
//   - no site → `-` (sentinel) so the URL stays three segments and
//              the view segment is always at index 2.
//
// The route catch-all `/*` in main.tsx swallows every path we want,
// so we can navigate freely under the wildcard without registering
// each shape with react-router.
// ---------------------------------------------------------------------------
const ORG_SLUG_MAP: Record<string, string> = {
  // Same minetwin house org Cloud Lead uses for everything; see
  // useDatabricksTaxonomy.ORG_CATALOG_MAP for the parallel mapping.
  org_01KWJF5AT8N0YRT219MRH1FM6T: 'minetwin',
};

// URL segment from the RESOLVED org slug (MULTI-ORG-PORTAL plan): the module
// slug `minetwin-default` reads as `minetwin` in URLs (back-compatible with
// the map above), and other orgs shed the stamp prefix — `minetwin-south32`
// → `south32`. The map still wins for explicitly-curated entries. Lives in
// services/orgFromUrl because OrgContext also inverts it: a page load whose
// URL names a different member org switches the session to it.

// Module-scoped memory for the URL sync effect. Lives outside React
// state on purpose: `ProjectScopedSpacetime` keys its inner provider on
// `projectName` (SpacetimeProvider.tsx), so every site pick unmounts
// and remounts the entire App subtree. Anything in App-local React
// state (useState/useRef) is reset on remount; a module-level object
// survives. Resets only on a real page reload.
const urlSyncMemory = {
  /** Last `location.pathname` URL → state has reconciled with. Used
   *  to skip work when the effect re-fires on a key-driven remount
   *  (pathname unchanged from before the remount). */
  lastPathnameSeen: null as string | null,
};

function deriveOrgSlug(
  organizationId: string | null | undefined,
  resolvedOrgSlug?: string,
): string {
  if (organizationId && ORG_SLUG_MAP[organizationId]) {
    return ORG_SLUG_MAP[organizationId];
  }
  if (resolvedOrgSlug) return urlSlugFromOrgSlug(resolvedOrgSlug);
  if (!organizationId) return 'org';
  // Fallback: lowercase + take the first ~10 chars of the suffix so
  // unmapped orgs still get a readable URL segment. Unique within an
  // org; deterministic across sessions.
  return organizationId.toLowerCase().replace(/^org_/, '').slice(0, 10);
}

const NO_SITE = '-';
const KNOWN_VIEWS: ReadonlyArray<ViewKey> = [
  'live',
  'workforce',
  'cohort-builder',
  'cohorts',
  'orientations',
  'orientation-editor',
  'review-queue',
  'leadership',
  'members',
  'settings',
  'trainee',
];

function isViewKey(v: string): v is ViewKey {
  return (KNOWN_VIEWS as ReadonlyArray<string>).includes(v);
}

type ParsedPath = {
  orgSlug: string | null;
  siteSlug: string | null; // `null` when sentinel `-`
  view: ViewKey | null;
};

function parsePath(pathname: string): ParsedPath {
  const segs = pathname.replace(/^\/+/, '').replace(/\/+$/, '').split('/');
  const [org, site, view] = segs;
  return {
    orgSlug: org || null,
    siteSlug: !site || site === NO_SITE ? null : site,
    view: view && isViewKey(view) ? view : null,
  };
}

function buildPath(orgSlug: string, siteSlug: string | null, view: ViewKey): string {
  return `/${orgSlug}/${siteSlug ?? NO_SITE}/${view}`;
}

export function App() {
  const { tweaks, update: updateTweaks } = useTweaks();
  const { toasts, push: toast, dismiss, notifications, unreadCount, markAllRead, clearNotifications } = useToasts();
  // Bell = session feed (retained toasts, above) merged with the DURABLE
  // per-user feed from the console module (assigned / completed / welcome —
  // emitted server-side, survives reloads). Read/clear fan out to both.
  const durable = useDurableNotifications();
  const bellItems = useMemo(
    () =>
      [...notifications, ...durable.items].sort(
        // Newest first; same-transaction durable rows (equal ts) order by the
        // server's auto_inc seq NUMERICALLY (the string id would put
        // stdb-100 before stdb-99); string ids only as a stable last resort.
        (a, b) => b.ts - a.ts || (b.seq ?? 0) - (a.seq ?? 0) || (a.id < b.id ? 1 : -1),
      ),
    [notifications, durable.items],
  );
  const bellUnread = unreadCount + durable.unreadCount;
  const bellMarkAllRead = useCallback(() => {
    markAllRead();
    durable.markAllRead();
  }, [markAllRead, durable.markAllRead]);
  const bellClearAll = useCallback(() => {
    clearNotifications();
    durable.clearAll();
  }, [clearNotifications, durable.clearAll]);
  const user = useAuth();
  const auth = useAuthKit();
  const orgSession = useOrgSession();
  // Phase 3 follow-up: backfill user.display_name with AuthKit's First+Last
  // so the roster shows real names instead of the email fallback that
  // client_connected writes when the JWT has no `name` claim.
  useDisplayNameSync();
  // Auto-allowlist the current portal hostname on the launcher's
  // CORS-origin table so its 127.0.0.1:19871 HTTP API accepts our
  // Launch fetches. Silently no-ops for non-launcher-admins and for
  // origins that are already allowlisted.
  useLauncherCorsSelfAllowlist();

  // Sites come from the org module's `project` table. While the org
  // subscription is still loading, `sites` may be empty — we surface that
  // as `site: undefined` and pages that need a site show a loading state.
  const { sites } = useProjects();
  // Default site policy: prefer `minezero-sandbox` when the user has no
  // explicit selection (tweaks.siteIndex === -1, the sentinel). Falls
  // back to `sites[0]` if minezero-sandbox isn't in the user's project
  // list. Once the user picks a site from the topbar, `tweaks.siteIndex`
  // becomes a real index >= 0 and this fallback is bypassed.
  const effectiveSiteIndex =
    tweaks.siteIndex >= 0
      ? tweaks.siteIndex
      : findDefaultSiteIndex(sites);
  const site: Site | undefined = sites[effectiveSiteIndex] ?? sites[0];
  // Per-site rights (ORIENTATION-ROLES P2): which pages open, and where a
  // first visit lands.
  const access = useSiteAccess(site?.projectName ?? '');
  const landing = landingView(access);

  const location = useLocation();
  const navigate = useNavigate();
  const orgSlug = useMemo(
    () => deriveOrgSlug(auth.organizationId, orgSession.org.slug),
    [auth.organizationId, orgSession.org.slug],
  );

  // True while the view is only a default (no URL view, nothing stored):
  // re-landed once the site rights have loaded.
  const [landingPending, setLandingPending] = useState(
    () => !parsePath(window.location.pathname).view && readStoredView(landing) === null,
  );
  const [{ view, ctx }, setViewState] = useState<ViewState>(() => {
    // URL takes precedence over localStorage so refreshes on a deep
    // link land on the right page. Fall back to localStorage when the
    // URL doesn't carry a known view (e.g. fresh login lands on `/`).
    const parsed = parsePath(window.location.pathname);
    if (parsed.view) return { view: parsed.view, ctx: {} };
    return readStoredView(landing) ?? { view: landing, ctx: {} };
  });
  const [cmdOpen, setCmdOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  // Sidebar collapse (icon rail): persisted so the choice survives reloads.
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try { return localStorage.getItem('mt_nav_collapsed') === '1'; } catch { return false; }
  });
  const toggleNav = useCallback(() => {
    setNavCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem('mt_nav_collapsed', next ? '1' : '0'); } catch { /* best-effort */ }
      return next;
    });
  }, []);

  // Surface SDK-side reducer rejections (e.g. SenderError from the server)
  // as toasts. Currently most relevant for the MineZero #10 cutover blocker:
  // pre-cutover, `update_property_string` with a dotted path errors with
  // "Component 'assignedOrientation' not found ..."; post-cutover the same
  // call resolves to the rel-property and succeeds.
  useEffect(() => {
    function handler(e: PromiseRejectionEvent) {
      const reason = e.reason;
      const name = reason?.name ?? 'Error';
      const message = reason?.message ?? String(reason);
      if (name === 'SenderError' || /SpacetimeDB|SenderError/.test(message)) {
        toast({
          title: 'SpacetimeDB rejected the request',
          sub: message.slice(0, 200),
          kind: 'bad',
        });
        e.preventDefault();
      }
    }
    window.addEventListener('unhandledrejection', handler);
    return () => window.removeEventListener('unhandledrejection', handler);
  }, [toast]);

  useEffect(() => {
    localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify({ view, ctx }));
  }, [view, ctx]);

  // URL ↔ state sync — one-way design (URL is authoritative).
  //
  // Rewritten 2026-06-22 (third pass). Previous two-effect (URL → state
  // + URL ← state) versions both lost to React's StrictMode double-
  // invoke in dev: URL ← state would set URL_LAST_KEY=desired ahead of
  // `navigate()`, but the second pass of URL → state then read the
  // updated marker against the STILL-OLD pathname (closure value, since
  // navigation hadn't propagated yet), declared a mismatch, and
  // "adopted" the old URL back into state — visible as the topbar
  // dropdown flipping to the new site, then snapping back to the old
  // one. The race only exists when BOTH effects can write the shared
  // pathname-of-record while the browser pathname is still in flight.
  //
  // Fix: collapse to one effect. State changers (topbar site picker,
  // sidebar nav, ⌘1–⌘5, command palette) call `navigate()` themselves
  // alongside the React state update, so the URL is updated at the
  // same beat as state. The remaining effect (URL → state) only runs
  // when `location.pathname` actually changes — initial mount, deep
  // links, browser back/forward — and adopts URL into state. It also
  // backfills missing URL segments (e.g., visiting `/` lands the user
  // on `/<org>/<site>/<view>` via `replace: true`).
  //
  // The `lastPathnameSeen` marker lives at MODULE scope (outside the
  // React tree) so it survives the key-driven App remount that fires
  // every time `tweaks.siteIndex` changes — see
  // `ProjectScopedSpacetime` in SpacetimeProvider.tsx.

  useEffect(() => {
    if (sites.length === 0) return;
    if (urlSyncMemory.lastPathnameSeen === location.pathname) return;

    const parsed = parsePath(location.pathname);
    let nextView = view;
    let nextSiteIdx = tweaks.siteIndex;

    if (parsed.view && parsed.view !== view) {
      setViewState({ view: parsed.view, ctx: {} });
      nextView = parsed.view;
    }
    if (parsed.siteSlug) {
      const idx = sites.findIndex((s) => s.projectName === parsed.siteSlug);
      if (idx >= 0 && idx !== tweaks.siteIndex) {
        updateTweaks({ siteIndex: idx });
        nextSiteIdx = idx;
      }
    } else if (tweaks.siteIndex < 0) {
      // No site in URL AND no explicit selection persisted — adopt the
      // default (`minezero-sandbox` if present, else sites[0]) so the
      // canonicalized URL matches what the rest of the app will show.
      const preferredIdx = findDefaultSiteIndex(sites);
      updateTweaks({ siteIndex: preferredIdx });
      nextSiteIdx = preferredIdx;
    }

    // Canonicalize: if the URL is missing pieces (e.g. `/`, or `/org/-/...`),
    // backfill to the full `/org/site/view` form. Use the post-adoption
    // (next*) values so we don't publish a closure-stale URL that URL →
    // state then has to reverse.
    const nextSite = sites[nextSiteIdx];
    const canonical = buildPath(orgSlug, nextSite?.projectName ?? null, nextView);
    if (location.pathname !== canonical) {
      urlSyncMemory.lastPathnameSeen = canonical;
      navigate(canonical, { replace: true });
    } else {
      urlSyncMemory.lastPathnameSeen = location.pathname;
    }
    // `view`, `tweaks.siteIndex`, `site`: state we WRITE from here.
    // `sites` full-array reference churns every STDB tick — `sites.length`
    // is enough to know the catalog is ready without re-firing on ticks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, sites.length, orgSlug, auth.organizationId, navigate]);

  // State-changers double-write (state + URL) so the one-way URL → state
  // effect doesn't have to publish state-derived URLs in a separate
  // effect (which races with itself under StrictMode double-invoke).
  // `go(v)` walks the sidebar/keyboard/command-palette view changes;
  // `onSiteChange` (passed to Topbar below) walks the site picker.
  const go = useCallback((v: ViewKey, payload: unknown = null, extraCtx: Record<string, unknown> = {}) => {
    setViewState({ view: v, ctx: { payload, ...extraCtx } });
    if (auth.organizationId) {
      navigate(buildPath(orgSlug, site?.projectName ?? null, v));
    }
  }, [orgSlug, site?.projectName, auth.organizationId, navigate]);

  // Global keyboard shortcuts. ⌘1–⌘5 walk the admin workflow left-to-right;
  // ⌘6–⌘8 cover the orientation-admin surfaces; ⌘9 opens Settings and
  // ⌘0 jumps to the trainee surface (both always available). Non-admins
  // pressing an admin digit just land on the trainee fallback — same as
  // deep-linking the URL.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'k') { e.preventDefault(); setCmdOpen(true); }
      else if (mod && e.key === '1') { e.preventDefault(); go('live'); }
      else if (mod && e.key === '2') { e.preventDefault(); go('workforce'); }
      else if (mod && e.key === '3') { e.preventDefault(); go('cohort-builder'); }
      else if (mod && e.key === '4') { e.preventDefault(); go('review-queue'); }
      else if (mod && e.key === '5') { e.preventDefault(); go('leadership'); }
      else if (mod && e.key === '6') { e.preventDefault(); go('orientations'); }
      else if (mod && e.key === '7') { e.preventDefault(); go('orientation-editor'); }
      else if (mod && e.key === '8') { e.preventDefault(); go('members'); }
      else if (mod && e.key === '9') { e.preventDefault(); go('settings'); }
      else if (mod && e.key === '0') { e.preventDefault(); go('trainee'); }
      else if (mod && e.key.toLowerCase() === 'l') {
        e.preventDefault();
        updateTweaks({ theme: tweaks.theme === 'dark' ? 'light' : 'dark' });
      }
      else if (mod && e.key.toLowerCase() === 'b') { e.preventDefault(); toggleNav(); }
      else if (e.key === 'Escape') { setCmdOpen(false); setHelpOpen(false); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tweaks.theme, cmdOpen, go, updateTweaks, toggleNav]);

  // First visit: land by the highest right once the rights are known.
  useEffect(() => {
    if (!landingPending || !access.isReady) return;
    setLandingPending(false);
    if (landing !== view) go(landing);
  }, [landingPending, access.isReady, landing, view, go]);

  const canOpen = useCallback((v: ViewKey) => canOpenView(v, access), [access]);

  // A page this user may not open (or an unknown / stale ViewKey from
  // localStorage, e.g. an old 'dashboard') shows the landing page for
  // their rights instead of a blank page.
  function pageFor(v: ViewKey): React.ReactNode {
    switch (v) {
      case 'live': return <LiveSessions toast={toast} />;
      case 'workforce': return <Workforce toast={toast} />;
      case 'cohort-builder': return <CohortBuilder toast={toast} onOpenSettings={() => go('settings')} />;
      case 'cohorts': return <Cohorts toast={toast} />;
      case 'orientations': return <Orientations toast={toast} />;
      case 'orientation-editor': return <OrientationEditor toast={toast} />;
      case 'review-queue': return <ReviewQueue toast={toast} />;
      case 'leadership': return <Leadership toast={toast} />;
      case 'members': return <Members toast={toast} />;
      case 'settings': return <Settings toast={toast} />;
      default: return <TraineePortal toast={toast} />;
    }
  }
  const Page = pageFor(canOpen(view) ? view : landing);

  return (
    <div className={'app' + (navCollapsed ? ' nav-collapsed' : '')} data-screen-label={'01 ' + view}>
      <Topbar
        site={site}
        sites={sites}
        siteIndex={tweaks.siteIndex}
        onSiteChange={(i) => {
          updateTweaks({ siteIndex: i });
          const newSiteSlug = sites[i]?.projectName ?? null;
          if (auth.organizationId) {
            navigate(buildPath(orgSlug, newSiteSlug, view));
          }
        }}
        onOpenCmd={() => setCmdOpen(true)}
        onOpenHelp={() => setHelpOpen(true)}
        onOpenBugReport={() => setBugReportOpen(true)}
        onToggleTheme={() => updateTweaks({ theme: tweaks.theme === 'dark' ? 'light' : 'dark' })}
        theme={tweaks.theme}
        user={user}
        onNav={go}
        notifications={bellItems}
        unreadCount={bellUnread}
        onMarkNotificationsRead={bellMarkAllRead}
        onClearNotifications={bellClearAll}
      />
      <Sidebar view={view} onNav={go} site={site} canOpen={canOpen} collapsed={navCollapsed} onToggleCollapsed={toggleNav} theme={tweaks.theme} />
      <main className="main">
        <ConnectionBanner hasSite={sites.length > 0} />
        {access.siteAdmin && <StuckProvisioningBanner onOpenSettings={() => go('settings')} />}
        {Page}
      </main>
      <Toasts toasts={toasts} dismiss={dismiss} />
      {cmdOpen && <CmdPalette onClose={() => setCmdOpen(false)} onNav={go} canOpen={canOpen} />}
      {helpOpen && <HelpOverlay onClose={() => setHelpOpen(false)} />}
      <BugReportDialog isOpen={bugReportOpen} onClose={() => setBugReportOpen(false)} />
    </div>
  );
}
